/**
 * Generation du terrain et des biomes.
 *
 * Tout est deterministe et sans dependance au rendu: le serveur et le client
 * obtiennent exactement le meme monde pour une meme graine. Les hauteurs et les
 * biomes sont precalcules dans une grille de 501x501, les lectures sont ensuite
 * instantanees (bilineaire pour la hauteur, plus proche voisin pour le biome).
 *
 * Disposition: plage a l'ouest, glacier au nord, desert au sud, montagne a
 * l'est, foret au centre. Les frontieres sont deformees par du bruit.
 *
 * Le monde est une ILE: les quatre bords finissent dans l'eau. Le trait de cote
 * est une courbe deformee sur chaque bord (anses, criques, pointes), les quatre
 * courbes sont reunies par un minimum ADOUCI pour que les coins soient des caps
 * arrondis et non des angles droits, et chaque biome traite sa rencontre avec la
 * mer a sa maniere: greve de sable a l'ouest et au sud, falaise de glace et
 * banquise au nord, falaises et ecueils a l'est.
 *
 * A l'interieur: plateaux, cretes, vallees et cuvettes, trois lacs sous le
 * niveau de la mer et deux cours d'eau qui descendent vers l'ocean.
 */
import { CARTE_X, CARTE_Z, CHUNK_SIZE, MAX_WALK_SLOPE, SEA_LEVEL, TERRAIN_CELL, WORLD_SIZE } from '../constants.ts'
import { BIOMES } from '../data/biomes.ts'
import type { BiomeId, Prop, PropKind, SolId, TerrainSample, ITerrain } from '../types/world.ts'
import type { RegionDef } from '../types/ile2.ts'
import { Alea, bruitValeur, clamp, dist, fbm, fbmCrete, hache2, lerp, melangerCouleur, smoothstep } from './alea.ts'
import { construireTable, lireTable, minAdouci } from './relief.ts'

/** Cote de la grille de hauteur. */
const N = WORLD_SIZE / TERRAIN_CELL + 1

/** Ordre fixe des biomes: sert d'index dans la grille et dans les tampons. */
const ORDRE: readonly BiomeId[] = ['plage', 'foret', 'montagne', 'glacier', 'desert']

/** Sous ce poids, un biome n'est pas evalue: il ne changerait rien de visible. */
const POIDS_MIN = 0.004

/** Centre du monde, aplani pour que le joueur y apparaisse. */
const CENTRE = WORLD_SIZE / 2
const RAYON_PLAT = 12
const RAYON_FONDU = 42

// ------------------------------------------------------- champs a une variable

/**
 * Les champs a une variable et le minimum adouci vivent desormais dans
 * `shared/sim/relief.ts`: l'ile 2 en avait besoin mot pour mot, et deux copies
 * d'une meme regle finissent toujours par diverger. Rien n'a change de valeur —
 * c'est le test « l'ile 1 ne bouge pas d'un centimetre » qui le prouve.
 */
function construireTableMonde(graine: number, f: (u: number, graine: number) => number): Float64Array {
  return construireTable(WORLD_SIZE, graine, f)
}

/**
 * Deplacement d'une frontiere, le long de celle-ci: grandes anses de plusieurs
 * dizaines de metres plus un decoupage plus fin, pour qu'aucun bord de biome ne
 * ressemble a une ligne droite.
 */
function deformer(u: number, graine: number): number {
  return (fbm(u * 0.0085, 11.5, graine, 3) - 0.5) * 105 + (fbm(u * 0.028, 27.5, graine + 31, 2) - 0.5) * 32
}

/**
 * Retrait du trait de cote par rapport au bord du monde, en metres.
 *
 * La valeur renvoyee est une FORME, pas une distance: `tablesCote` la remet
 * ensuite a l'echelle pour que la MOYENNE des quatre bords vaille exactement
 * CIBLE_COTE. C'est ce qui permet de depenser l'eau ou elle se voit: la surface
 * d'eau prise sur le monde jouable est fixee d'avance, donc creuser un golfe
 * quelque part rapproche automatiquement la cote du bord ailleurs.
 *
 * Trois echelles de bruit empilees (anses de 130 m, criques de 36 m, dentelle
 * de 13 m) moins un socle: la ou la somme passe sous le socle, la cote vient
 * coller le bord du monde et forme une pointe. Puis DEUX GOLFES par bord, des
 * gaussiennes etroites et tres profondes. Sans eux, onze metres de retrait
 * moyen etalés uniformement donnaient exactement ce que le proprietaire ne
 * voulait pas: un liseré rectangulaire.
 */
function insetCote(u: number, graine: number): number {
  const anses = fbm(u * 0.0077, 4.5, graine, 3)
  const criques = fbm(u * 0.028, 9.5, graine + 23, 3)
  const dentelle = fbm(u * 0.077, 21.5, graine + 47, 2)
  let forme = anses + criques * 0.52 + dentelle * 0.24 - 0.74
  for (let g = 1; g <= 2; g++) {
    const centre = hache2(g, 7, graine) * WORLD_SIZE
    const demi = 22 + hache2(g, 11, graine) * 30
    const e = (u - centre) / demi
    forme += (1.5 + hache2(g, 17, graine)) * Math.exp(-e * e * 2.2)
  }
  return Math.max(0.06, forme)
}

// ------------------------------------------------------------------ criques

/**
 * Une cote entierement en falaise est un piege, et le pire genre: silencieux.
 *
 * Le joueur sait nager, il plonge donc n'importe ou, et il faut qu'il puisse
 * ressortir. Or `GREVE` / `CHUTE_COTE` posent, sur les cotes escarpees, le trait
 * d'eau au MILIEU du plongeon: le relief y descend de quatre a dix metres en
 * moins de huit, la pente au niveau de la mer depasse MAX_WALK_SLOPE, et le
 * nageur vient buter contre le rivage sans jamais y monter. Mesure faite sur la
 * graine par defaut: 166 m de cote est d'affilee sans une seule sortie, 144 m au
 * nord, 160 m au sud.
 *
 * On seme donc le long de chaque bord des CRIQUES: des entailles ou le rivage
 * recule (RETRAIT_CRIQUE) et ou le profil cotier se fond vers celui d'une greve
 * douce — greve au ras de l'eau, plongeon etale, ecueils et banquise effaces.
 * Une crique par creneau de PAS_CRIQUE metres, decalee dans son creneau: le
 * creneau borne l'espacement, donc la plus longue muraille possible, tandis que
 * le decalage, la largeur et la profondeur tires de la graine font qu'aucune
 * crique ne ressemble a la precedente.
 *
 * Ce n'est pas qu'un correctif: une falaise percee de petites anses se lit
 * beaucoup mieux qu'une muraille continue.
 */
const PAS_CRIQUE = 30

/** Decalage d'une crique dans son creneau, en fraction de PAS_CRIQUE. */
const DECALAGE_CRIQUE = 0.15

/** Demi-largeur d'une crique: de la simple entaille a la vraie anse. */
const DEMI_CRIQUE_MIN = 4.5
const DEMI_CRIQUE_MAX = 11

/**
 * Recul du rivage au creux d'une crique, en metres, et distance a laquelle ce
 * recul s'est completement efface dans les terres.
 *
 * Pourquoi un recul, alors qu'un profil doux devrait suffire: sur la moitie du
 * perimetre le trait de cote passe a moins de cinq metres du bord de la carte, et
 * tout le profil cotier se joue alors dans les six derniers metres que
 * `noyerBordure` ecrase en une marche de deux metres pour un. Une crique doit
 * mordre dans la terre pour que la mer y entre pour de vrai.
 *
 * Le recul est COMPENSE: partout ailleurs le rivage avance de RETRAIT_CRIQUE fois
 * la force moyenne des criques, soit un peu plus de quatre metres. Le trait de
 * cote est ainsi festonne au lieu d'avoir simplement recule, et la surface d'eau
 * du monde ne bouge presque pas (mesure: +1,8 %; ce qui reste vient des endroits
 * ou la cote colle deja le bord de la carte et ou l'avancee n'a nulle part a
 * aller).
 *
 * Et il s'efface avant FONDU_CRIQUE metres dans les terres. C'est la contrainte
 * la plus dure du correctif: le proprietaire a des batiments poses dans son monde
 * sauvegarde, deplacer le sol sous eux serait inacceptable. Deformer le trait de
 * cote lui-meme aurait fait glisser toute la bande littorale — jusqu'a 66 m dans
 * les terres sur la plage; en eteignant tout avant 44 m, plus rien ne bouge
 * au-dela de 55 m du rivage.
 */
const RETRAIT_CRIQUE = 13
const FONDU_CRIQUE = 44

/**
 * Retrait du rivage au-dela duquel une crique n'a plus besoin de mordre dans la
 * terre: son profil doux suffit.
 *
 * Le recul ne sert qu'a echapper a `noyerBordure`. Devant un golfe, ou le trait de
 * cote est deja loin du bord de la carte, il n'apporterait rien et ne ferait que
 * noyer l'ile un peu plus pour rien.
 */
const RIVAGE_LARGE = 14

/**
 * Ecart de distance au-dela duquel un bord ne gouverne plus le rivage: sa table
 * de criques cesse alors de s'appliquer. Aux quatre coins, les deux bords
 * voisins comptent tous les deux et le cap recoit les criques des deux tables.
 */
const GOUVERNE_COTE = 44

/**
 * Force des criques le long d'un bord, dans [0, 1], tabulee au metre.
 *
 * Le creux de chaque crique est PLAT sur un bon tiers de sa largeur. Ce n'est pas
 * cosmetique: `pente` mesure la norme du gradient, donc AUSSI la variation le
 * long de la cote. Une entaille en pointe aurait des parois plus raides que la
 * falaise qu'elle perce, et n'aurait servi a rien.
 */
function criquesCote(u: number, graine: number): number {
  let f = 0
  const creneau = Math.floor(u / PAS_CRIQUE)
  for (let i = creneau - 1; i <= creneau + 1; i++) {
    const centre = (i + 0.5) * PAS_CRIQUE + (hache2(i, 3, graine) - 0.5) * 2 * DECALAGE_CRIQUE * PAS_CRIQUE
    const demi = DEMI_CRIQUE_MIN + hache2(i, 5, graine) * (DEMI_CRIQUE_MAX - DEMI_CRIQUE_MIN)
    const force = 0.9 + hache2(i, 9, graine) * 0.1
    const v = force * smoothstep(1, 0.35, Math.abs(u - centre) / demi)
    if (v > f) f = v
  }
  return f
}

/** Retrait moyen du rivage, en metres. Fixe la surface d'eau du monde. */
const CIBLE_COTE = 9.5

/** Les quatre traits de cote, ramenes a la moyenne voulue. */
function tablesCote(seed: number): Float64Array[] {
  const tables = [
    construireTableMonde(seed + 2101, insetCote),
    construireTableMonde(seed + 2102, insetCote),
    construireTableMonde(seed + 2103, insetCote),
    construireTableMonde(seed + 2104, insetCote),
  ]
  let somme = 0
  let n = 0
  for (const t of tables) {
    for (let i = 0; i < t.length; i++) {
      somme += t[i] ?? 0
      n++
    }
  }
  const facteur = somme > 1e-6 ? (CIBLE_COTE * n) / somme : 1
  for (const t of tables) {
    for (let i = 0; i < t.length; i++) t[i] = clamp((t[i] ?? 0) * facteur, 1.5, 74)
  }
  return tables
}

/** Moyenne d'un jeu de tables. Sert a compenser le recul des criques. */
function moyenneTables(tables: readonly Float64Array[]): number {
  let somme = 0
  let n = 0
  for (const t of tables) {
    for (let i = 0; i < t.length; i++) {
      somme += t[i] ?? 0
      n++
    }
  }
  return n > 0 ? somme / n : 0
}

/**
 * Le terrain ne doit JAMAIS affleurer au bord de la carte: la grille s'arrete a
 * 0 et a 500, et une pointe qui y arriverait montrerait sa tranche. Les huit
 * derniers metres sont donc ramenes sous l'eau, ce qui les transforme en
 * hauts-fonds que l'ocean recouvre.
 */
function noyerBordure(x: number, z: number, h: number): number {
  const bord = Math.min(x, z, WORLD_SIZE - x, WORLD_SIZE - z)
  if (bord >= 6) return h
  const plafond = lerp(-1.8, h, smoothstep(1, 6, bord))
  return h < plafond ? h : plafond
}

interface Tables {
  fPlage: Float64Array
  fGlacier: Float64Array
  fDesert: Float64Array
  fMontagne: Float64Array
  cOuest: Float64Array
  cEst: Float64Array
  cNord: Float64Array
  cSud: Float64Array
  kOuest: Float64Array
  kEst: Float64Array
  kNord: Float64Array
  kSud: Float64Array
  /** Force moyenne des criques: le recul se mesure par rapport a elle. */
  moyCrique: number
}

let grainesTables = NaN
let tablesCourantes: Tables | null = null

function tablesPour(seed: number): Tables {
  if (seed === grainesTables && tablesCourantes) return tablesCourantes
  const criques = [
    construireTableMonde(seed + 3101, criquesCote),
    construireTableMonde(seed + 3102, criquesCote),
    construireTableMonde(seed + 3103, criquesCote),
    construireTableMonde(seed + 3104, criquesCote),
  ]
  const cotes = tablesCote(seed)
  const vide = new Float64Array(WORLD_SIZE + 1)
  const t: Tables = {
    fPlage: construireTableMonde(seed + 1301, deformer),
    fGlacier: construireTableMonde(seed + 1302, deformer),
    fDesert: construireTableMonde(seed + 1303, deformer),
    fMontagne: construireTableMonde(seed + 1304, deformer),
    cOuest: cotes[0] ?? vide,
    cEst: cotes[1] ?? vide,
    cNord: cotes[2] ?? vide,
    cSud: cotes[3] ?? vide,
    kOuest: criques[0] ?? vide,
    kEst: criques[1] ?? vide,
    kNord: criques[2] ?? vide,
    kSud: criques[3] ?? vide,
    moyCrique: moyenneTables(criques),
  }
  grainesTables = seed
  tablesCourantes = t
  return t
}

// ------------------------------------------------------------------ frontieres

/**
 * Poids de chaque biome, dans l'ordre de ORDRE, ecrits dans `sortie`.
 * Les bornes nominales (74 / 426) sont deplacees par `deformer`, et la
 * transition est etalee sur 26 m pour que les couleurs se fondent.
 */
function calculerPoids(x: number, z: number, seed: number, sortie: Float64Array): void {
  const t = tablesPour(seed)
  const bPlage = 74 + lireTable(t.fPlage, z)
  const bGlacier = 74 + lireTable(t.fGlacier, x)
  const bDesert = 426 + lireTable(t.fDesert, x)
  const bMontagne = 426 + lireTable(t.fMontagne, z)

  const wPlage = smoothstep(bPlage + 13, bPlage - 13, x)
  const wGlacier = smoothstep(bGlacier + 13, bGlacier - 13, z)
  const wDesert = smoothstep(bDesert - 13, bDesert + 13, z)
  const wMontagne = smoothstep(bMontagne - 13, bMontagne + 13, x)
  const wForet = (1 - wPlage) * (1 - wGlacier) * (1 - wDesert) * (1 - wMontagne)

  const total = wPlage + wForet + wMontagne + wGlacier + wDesert
  const inv = total > 1e-6 ? 1 / total : 0
  sortie[0] = wPlage * inv
  sortie[1] = wForet * inv
  sortie[2] = wMontagne * inv
  sortie[3] = wGlacier * inv
  sortie[4] = wDesert * inv
}

// ------------------------------------------------------------------ littoral

const ADOUCI_COTE = 38

/**
 * Altitude de la greve, largeur de l'adoucissement vers l'interieur et
 * distance de chute sous le rivage, dans l'ordre de ORDRE.
 *
 * C'est ce tableau qui donne son caractere a chaque cote: la plage et le desert
 * s'aplatissent sur 60 m et glissent doucement sous l'eau; le glacier garde sa
 * hauteur jusqu'au dernier moment puis tombe en 4 m, ce qui fait une falaise de
 * glace; la montagne fait de meme un peu moins brutalement, ce qui fait des
 * falaises et des cotes rocheuses.
 */
const GREVE: readonly number[] = [1.0, 2.4, 4.2, 9.5, 1.6]
const LARGEUR_COTE: readonly number[] = [66, 50, 44, 26, 62]
const CHUTE_COTE: readonly number[] = [16, 10, 8, 4.5, 15]

/**
 * Meme triplet, pour une crique a pleine force (voir `criquesCote`).
 *
 * La greve descend au ras de l'eau: le trait d'eau se pose alors sur le PALIER du
 * profil, ou la pente est nulle des deux cotes, au lieu du milieu du plongeon.
 * Le plongeon s'etale, et la largeur d'adoucissement suffit pour remonter a pied
 * depuis l'anse — sans jamais depasser les 66 m de la plage, donc sans toucher a
 * l'interieur des terres.
 */
const GREVE_CRIQUE = 0.55
const LARGEUR_CRIQUE = 46
const CHUTE_CRIQUE = 17

// ------------------------------------------------------------------ relief

/** Plage: pente tres douce vers l'est, dunes basses. */
function hPlage(x: number, z: number, seed: number): number {
  const dunes = fbm(x * 0.026, z * 0.026, seed + 311, 3) - 0.5
  const grain = fbm(x * 0.12, z * 0.12, seed + 312, 2) - 0.5
  return 2.2 + 8.5 * smoothstep(14, 120, x) + dunes * 2.4 + grain * 0.4
}

/**
 * Foret: le coeur du monde, et donc la ou la topographie doit se lire.
 *
 * Trois echelles superposees, chacune avec un role:
 *   `plateaux` un champ tres large ecrase vers 0 ou 1 par un smoothstep etroit:
 *              on obtient de vrais REPLATS (ou l'on peut batir) separes par des
 *              escarpements, et des cuvettes, plutot qu'une houle continue.
 *   `sillons`  du bruit de crete signe: des cretes et, entre elles, des vallees
 *              qui descendent vraiment.
 *   `collines` la houle douce d'origine, qui evite que tout soit anguleux.
 * L'amplitude totale est doublee par rapport aux collines molles d'avant, tout
 * en restant sous la pente marchable: les escarpements sont larges de 20 m.
 */
function hForet(x: number, z: number, seed: number): number {
  const large = fbm(x * 0.0058, z * 0.0058, seed + 525, 3)
  const plateaux = (smoothstep(0.28, 0.74, large) - 0.5) * 9.5
  const sillons = (fbmCrete(x * 0.0075, z * 0.0075, seed + 524, 2) - 0.44) * 8
  const collines = fbm(x * 0.0135, z * 0.0135, seed + 522, 3) - 0.5
  const grain = fbm(x * 0.105, z * 0.105, seed + 523, 2) - 0.5
  // Replats. Par plaques d'une centaine de metres, le relief moyen et le grain
  // s'effacent presque: il reste une terrasse. C'est la qu'on batit. Sans elles,
  // un monde deux fois plus accidente ne laissait plus qu'un emplacement
  // constructible sur cinq — le jeu est aussi un jeu de construction.
  const replat = smoothstep(0.36, 0.7, fbm(x * 0.011, z * 0.011, seed + 526, 2))
  return 13 + plateaux + (sillons + collines * 5.6) * (1 - 0.85 * replat) + grain * 0.45 * (1 - 0.8 * replat)
}

/**
 * Montagne: cretes qui grandissent vers l'est, puis retombent vers la cote.
 *
 * Le bruit de crete est echantillonne sur un domaine deforme et une seconde
 * fois tourne de 37 degres: sans cela, ses plis se rangent sur les axes du
 * monde et le massif ressemble a un labyrinthe rectangulaire.
 *
 * `passe` module l'amplitude des cretes le long de couloirs tres allonges en x:
 * ce sont les COLS, des traversees basses et marchables du massif. Et `versMer`
 * fait redescendre le massif sur ses quarante derniers metres, pour qu'il
 * rencontre l'ocean en falaises et non en muraille de 60 m.
 */
function hMontagne(x: number, z: number, seed: number): number {
  const wx = x + (fbm(x * 0.0055, z * 0.0055, seed + 91, 2) - 0.5) * 120
  const wz = z + (fbm(x * 0.0055, z * 0.0055, seed + 92, 2) - 0.5) * 120
  const ax = wx * 0.0105
  const az = wz * 0.0105
  const rx = (ax * 0.7986 - az * 0.6018) * 1.6
  const rz = (ax * 0.6018 + az * 0.7986) * 1.6
  const crete = 0.64 * fbmCrete(ax, az, seed + 733, 3) + 0.36 * fbmCrete(rx, rz, seed + 736, 2)
  const passe = smoothstep(0.32, 0.66, fbm(x * 0.0035, z * 0.0125, seed + 737, 2))
  const gros = fbm(x * 0.008, z * 0.008, seed + 734, 3) - 0.5
  const detail = fbm(wx * 0.055, wz * 0.055, seed + 735, 3) - 0.5
  const rampe = smoothstep(386, 462, x)
  const versMer = 1 - 0.55 * smoothstep(450, 496, x)
  return 9 + gros * 7 + (crete * (22 + 44 * passe) + detail * 3.2) * rampe * versMer
}

/**
 * Glacier: plateau qui monte vers le nord, gradins, crevasses peu profondes.
 * La chute dans la mer froide est traitee par GREVE / CHUTE_COTE.
 */
function hGlacier(x: number, z: number, seed: number): number {
  const plateau = 10 * smoothstep(102, 16, z)
  const large = fbm(x * 0.0068, z * 0.0068, seed + 945, 2)
  const gradins = (smoothstep(0.38, 0.66, large) - 0.5) * 8
  const ondes = fbm(x * 0.016, z * 0.016, seed + 941, 3) - 0.5
  const wx = x + (fbm(x * 0.012, z * 0.012, seed + 943, 2) - 0.5) * 55
  const wz = z + (fbm(x * 0.012, z * 0.012, seed + 944, 2) - 0.5) * 55
  const crevasse = fbmCrete(wx * 0.038, wz * 0.038, seed + 942, 3)
  return 8.5 + plateau + gradins + ondes * 4.2 - crevasse * 2.1
}

/** Desert: dunes sinusoidales orientees, plus quelques mesas tabulaires. */
function hDesert(x: number, z: number, seed: number): number {
  const u = x * 0.9063 + z * 0.4226
  const derive = fbm(x * 0.012, z * 0.012, seed + 1151, 3)
  // Les dunes du desert s'aplatissent par plaques, comme un reg entre deux
  // cordons: c'est ce qui rend le sud constructible.
  const reg = smoothstep(0.44, 0.72, fbm(x * 0.012, z * 0.012, seed + 1154, 2))
  const dune = (Math.sin(u * 0.072 + derive * 7) * 4.4 + Math.sin(u * 0.19 + derive * 4) * 1.1) * (1 - 0.72 * reg)
  const large = fbm(x * 0.0058, z * 0.0058, seed + 1153, 2)
  const mesa = smoothstep(0.58, 0.72, large) * 9
  const grain = fbm(x * 0.085, z * 0.085, seed + 1152, 2) - 0.5
  return 10.5 + dune + mesa + (derive - 0.5) * 4.2 + grain * 0.7
}

// ------------------------------------------------------------------ hydrographie

interface Lac {
  x: number
  z: number
  r: number
  graine: number
}

interface Riviere {
  /** 'ouest': le cours suit x decroissant. 'sud': z croissant. */
  sens: 'ouest' | 'sud'
  /** Coordonnee le long du cours, a la source puis a l'embouchure. */
  depart: number
  arrivee: number
  /** Axe du lit, tabule au metre le long du cours. */
  centre: Float64Array
}

interface Ilot {
  x: number
  z: number
  r: number
  /** Altitude de la cime. Sous 1,3 m, l'ilot reste un haut-fond immerge. */
  cime: number
  graine: number
}

// ------------------------------------------------------------------ chute d'eau

/**
 * La chute d'eau: sa geometrie complete, deduite de la seule graine.
 *
 * Elle est PUBLIQUE parce que le rendu en a besoin. Une lame d'eau verticale ne
 * peut pas etre representee par la nappe posee au niveau de la mer, il faut un
 * maillage dedie: `client/render/chute.ts` lit cette description au lieu de
 * redeviner l'endroit a partir du champ de hauteur, ce qui serait une deuxieme
 * regle pour la meme question — le genre de divergence que ce projet a deja
 * payee cher.
 */
export interface Chute {
  /** Bascule: milieu de la crete, au ras du lit. */
  x: number
  z: number
  /** Sens du courant, unitaire, de l'amont vers le vide. */
  dx: number
  dz: number
  /** Altitude du lit a la bascule, donc le haut de la lame. */
  hCrete: number
  /** Demi-largeur du lit taille a plat, donc de la lame. */
  demiLit: number
  /** Fond plat de la vasque: centre, rayon et altitude. */
  vasqueX: number
  vasqueZ: number
  vasqueR: number
  vasqueFond: number
  /**
   * Altitude ou la lame TOUCHE l'eau, donc le bas de la chute.
   *
   * Elle valait `SEA_LEVEL` partout, en dur, dans sept endroits du rendu — ce
   * qui etait vrai tant qu'il n'y avait qu'une chute et qu'elle tombait dans la
   * mer. La cascade de l'ile 2 tombe a vingt metres d'altitude, sur une
   * plate-forme et non dans une fosse: l'eau de ce moteur est ce qui est sous
   * le niveau de la mer, donc une vasque perchee serait un puits creuse dans un
   * plateau — et le balayage du trait d'eau de l'ile 2 le refuserait, a raison.
   */
  hBassin: number
  /** Longueur du lit taille en amont, en metres. */
  longAmont: number
  /** Altitude du lit, tabulee au metre en REMONTANT le cours depuis la crete. */
  lit: Float64Array
}

/**
 * Ou chercher la chute: sur le pourtour nord-ouest du lac de la foret.
 *
 * Le lac est le seul plan d'eau interieur assez grand pour recevoir une chute,
 * il est deja borde de rives marchables, et il se vide dans l'ocean par la
 * riviere occidentale — la vasque est donc reliee au reste du monde sans qu'on
 * ait rien a creuser de plus. Le secteur nord-ouest n'est pas un hasard non
 * plus: le courant y descend vers le sud-est, et la camera regarde le monde
 * depuis le sud-est. Une falaise tournee dans l'autre sens serait cachee par le
 * plateau qui la surplombe, ce qui est exactement ce qu'il ne faut pas.
 */
const CHUTE_SECTEUR = Math.PI * 1.25
const CHUTE_DEMI_SECTEUR = Math.PI * 0.2
const CHUTE_ANGLES = 17

/** Recul de la bascule par rapport au rivage du lac, en metres. */
const CHUTE_RECUL_MIN = 14
const CHUTE_RECUL_MAX = 34

/** Sous cette hauteur de crete, le site ne vaut pas une chute: on n'en fait pas. */
const CHUTE_HAUTEUR_MIN = 5

/** Demi-largeur du lit taille a plat, en metres. */
const CHUTE_DEMI_LIT = 2.6

/** Enfoncement du lit sous le point bas de sa section, en metres. */
const CHUTE_CREUX = 0.55

/** Longueur du lit taille en amont, et distance sur laquelle il s'efface. */
const CHUTE_LONG_AMONT = 34
const CHUTE_FONDU_AMONT = 9

/** Debord du lit au-dela de la bascule, en metres. Voir `sculpterChute`. */
const CHUTE_DEBORD = 1.5

/**
 * Pentes admissibles du lit, en metres de montee par metre remonte.
 *
 * Le minimum garantit que l'eau descend VERS la bascule, meme la ou le relief
 * traverse creuse une cuvette. Le maximum est ce qui fait un RUISSEAU et non
 * une rampe: la levre tombe forcement sur l'epaule d'une pente, et un lit qui
 * suivait servilement le point bas de sa section remontait de deux metres
 * cinquante sur les cinq premiers metres — de l'eau qui devalait un talus, pas
 * un cours d'eau. Plafonner la pente creuse a la place une gorge qui
 * s'approfondit vers la chute.
 */
const CHUTE_PENTE_MIN = 0.02
const CHUTE_PENTE_MAX = 0.1

/** Fond et rayon de la vasque, en metres. */
const CHUTE_FOND = -2.8
const CHUTE_RAYON = 9

/**
 * Largeur des berges de la vasque, en metres.
 *
 * `CHUTE_MUR` est la berge du secteur AMONT: trois metres pour douze de
 * denivele, c'est la falaise. Partout ailleurs la berge vaut CHUTE_BERGE metres
 * par metre de denivele, plafonnee: la pente maximale d'un raccord en
 * smoothstep vaut une fois et demie sa pente moyenne, donc 2,4 m par metre
 * tient sous les 0,85 de MAX_WALK_SLOPE tant que le denivele reste modeste.
 * C'est par la, vers le lac, qu'on entre dans la vasque et qu'on en ressort.
 */
const CHUTE_MUR = 3
const CHUTE_BERGE = 2.4
const CHUTE_BERGE_MAX = 20

/** Bornes de la berge du LIT, plus etroites: la gorge n'est jamais profonde. */
const CHUTE_BERGE_LIT_MIN = 3
const CHUTE_BERGE_LIT_MAX = 16

/**
 * Denivele au-dela duquel la berge renonce et devient rocher.
 *
 * Une berge de vingt metres pour treize de denivele a une pente maximale de
 * 0,98: au-dessus de MAX_WALK_SLOPE, donc INFRANCHISSABLE — mais douce a l'oeil.
 * C'est le pire des deux mondes, un mur invisible, et c'est aussi beaucoup de
 * terre remuee pour rien. La ou le pays est trop haut, on assume donc la
 * falaise: la vasque devient un cirque de rocher, ouvert du seul cote ou le
 * terrain descend naturellement, c'est-a-dire vers le lac. C'est par la qu'on
 * entre et qu'on sort, a pied comme a la nage.
 */
const CHUTE_BERGE_RENONCE = 6

/**
 * Rayon au-dela duquel la chute ne touche plus a rien, en metres.
 *
 * Ce n'est pas une precaution de style: le proprietaire a des batiments poses
 * dans son monde sauvegarde, et deplacer le sol sous eux serait inacceptable.
 * Toutes les pieces de la chute (lit, falaise, vasque, berges) tiennent dans ce
 * disque, et `tests/unit/chute.test.ts` verifie sur le monde entier qu'au-dela
 * le relief est identique au bit pres.
 */
const CHUTE_PORTEE = 52

/** Nombre d'ilots cherches au large, et nombre d'essais avant d'abandonner. */
const ILOTS_VOULUS = 22
const ILOTS_ESSAIS = 9000

/**
 * Positions nominales des lacs, secouees par la graine.
 * Un lac dans la foret (source de la riviere occidentale), un a la lisiere du
 * desert (source de la riviere australe), un dans le glacier (lac de fonte).
 */
const LACS_NOMINAUX: readonly (readonly number[])[] = [
  [332, 158, 20],
  [302, 380, 17],
  [120, 46, 15],
]

// ------------------------------------------------------------------ classe

export class Terrain implements ITerrain {

  readonly seed: number

  /**
   * Ou l'ile est posee sur la carte. La premiere est a l'origine et le restera:
   * tout ce qui y est deja bati doit retrouver exactement le sol sous lequel il
   * a ete pose (voir shared/sim/carte.ts).
   */
  readonly x0 = 0
  readonly z0 = 0
  readonly cote = WORLD_SIZE

  private hauteurs: Float32Array
  private biomes: Uint8Array
  /** Hauteur imposee au centre du monde, pour garantir un depart plat. */
  private hCentre: number
  private tampon = new Float64Array(5)
  /** Distances signees aux quatre traits de cote: ouest, est, nord, sud. */
  private cotes = new Float64Array(4)
  private lacs: Lac[] = []
  private rivieres: Riviere[] = []
  private ilots: Ilot[] = []
  /**
   * La chute d'eau, ou null si la graine n'a offert aucun site.
   *
   * Elle reste nulle pendant `choisirChute`, qui interroge le relief BRUT: sans
   * cela le site se chercherait dans le terrain qu'il est en train de creuser.
   */
  private chuteChoisie: Chute | null = null

  /** Ou tombe l'eau, pour qui doit la dessiner. */
  get chute(): Chute | null {
    return this.chuteChoisie
  }

  constructor(seed: number) {
    this.seed = seed | 0
    this.hauteurs = new Float32Array(N * N)
    this.biomes = new Uint8Array(N * N)
    this.construireHydro()
    this.hCentre = this.brute(CENTRE, CENTRE)
    this.remplir()
  }

  // ---------------------------------------------------------------- generation

  /** Lacs et rivieres: tires de la graine, donc identiques partout. */
  private construireHydro(): void {
    const a = new Alea((this.seed ^ 0x1ac5_1e50) >>> 0)
    for (let i = 0; i < LACS_NOMINAUX.length; i++) {
      const base = LACS_NOMINAUX[i]
      if (!base) continue
      this.lacs.push({
        x: (base[0] ?? 0) + a.entre(-11, 11),
        z: (base[1] ?? 0) + a.entre(-11, 11),
        r: (base[2] ?? 20) * a.entre(0.92, 1.1),
        graine: (this.seed + 7300 + i * 131) | 0,
      })
    }

    // Riviere occidentale: sortie du lac de la foret, jusqu'a l'ocean.
    const lacForet = this.lacs[0]
    if (lacForet) {
      this.rivieres.push(this.faireRiviere('ouest', lacForet.x, lacForet.z, 4, (this.seed + 8801) | 0))
    }
    // Riviere australe: une source dans la foret, puis la traversee du desert.
    this.rivieres.push(this.faireRiviere('sud', 300 + a.entre(-14, 14), 152 + a.entre(-16, 16), WORLD_SIZE - 4, (this.seed + 8802) | 0))

    this.semerIlots(a)
    // Apres les ilots, et sans toucher au meme generateur: le site de la chute
    // se calcule, il ne se tire pas. Les ilots gardent donc exactement les
    // memes positions qu'avant son arrivee.
    this.chuteChoisie = this.choisirChute()
  }

  /**
   * Choisit le site de la chute sur le pourtour du lac de la foret.
   *
   * On balaie des rayons partant du centre du lac vers le nord-ouest. Sur
   * chacun, on releve le rivage, puis on essaie chaque recul admissible comme
   * bascule. Le site retenu est celui qui maximise, dans l'ordre d'importance:
   *
   *   - la hauteur de la crete, prise comme le POINT BAS de la section du lit.
   *     Prendre la hauteur au centre donnerait des levres posees en travers
   *     d'une pente, dont l'eau s'echapperait par le cote bas;
   *   - un pays qui MONTE en amont, sans quoi le lit devrait remonter pour
   *     rejoindre la crete et le ruisseau coulerait a l'envers;
   *   - la proximite du lac, qui recoit la vasque;
   *   - l'orientation, deja expliquee a CHUTE_SECTEUR.
   */
  private choisirChute(): Chute | null {
    const lac = this.lacs[0]
    if (!lac) return null

    let meilleur = -Infinity
    let site: { x: number; z: number; dx: number; dz: number; crete: number } | null = null
    for (let i = 0; i < CHUTE_ANGLES; i++) {
      const angle = CHUTE_SECTEUR + (i / (CHUTE_ANGLES - 1) - 0.5) * 2 * CHUTE_DEMI_SECTEUR
      // `u` va du lac vers les terres, le courant descend donc dans `-u`.
      const ux = Math.cos(angle)
      const uz = Math.sin(angle)
      const px = -uz
      const pz = ux

      // Rivage: le dernier point d'eau rencontre en s'eloignant du centre.
      let rivage = 0
      for (let d = lac.r * 0.4; d < lac.r * 3; d += 0.5) {
        if (this.brute(lac.x + ux * d, lac.z + uz * d) < 0.5) rivage = d
      }
      if (rivage === 0) continue

      for (let recul = CHUTE_RECUL_MIN; recul <= CHUTE_RECUL_MAX; recul += 0.5) {
        const d = rivage + recul
        const x = lac.x + ux * d
        const z = lac.z + uz * d
        if (x < CHUTE_PORTEE || z < CHUTE_PORTEE) break
        if (x > WORLD_SIZE - CHUTE_PORTEE || z > WORLD_SIZE - CHUTE_PORTEE) break
        const crete = this.pointBasLit(x, z, px, pz)
        const amont =
          (this.brute(x + ux * 12, z + uz * 12) +
            this.brute(x + ux * 22, z + uz * 22) +
            this.brute(x + ux * 32, z + uz * 32)) /
          3
        const note =
          crete +
          0.8 * Math.min(amont - crete, 3) -
          0.16 * recul +
          2.5 * Math.cos(angle - CHUTE_SECTEUR)
        if (note > meilleur) {
          meilleur = note
          site = { x, z, dx: -ux, dz: -uz, crete }
        }
      }
    }
    if (!site || site.crete < CHUTE_HAUTEUR_MIN + CHUTE_CREUX) return null

    // Le lit, tabule au metre en remontant le cours. Il est pose sous le point
    // BAS de chaque section, et on lui impose de monter en s'eloignant: un lit
    // qui suivrait servilement le terrain ferait remonter l'eau des que le
    // relief creuse une cuvette en chemin.
    const px = -site.dz
    const pz = site.dx
    const lit = new Float64Array(CHUTE_LONG_AMONT + 1)
    lit[0] = site.crete - CHUTE_CREUX
    for (let i = 1; i <= CHUTE_LONG_AMONT; i++) {
      const x = site.x - site.dx * i
      const z = site.z - site.dz * i
      const bas = this.pointBasLit(x, z, px, pz) - CHUTE_CREUX
      const precedent = lit[i - 1] ?? 0
      lit[i] = clamp(bas, precedent + CHUTE_PENTE_MIN, precedent + CHUTE_PENTE_MAX)
    }

    return {
      x: site.x,
      z: site.z,
      dx: site.dx,
      dz: site.dz,
      hCrete: lit[0] ?? 0,
      demiLit: CHUTE_DEMI_LIT,
      vasqueX: site.x + site.dx * (CHUTE_RAYON + CHUTE_MUR),
      vasqueZ: site.z + site.dz * (CHUTE_RAYON + CHUTE_MUR),
      vasqueR: CHUTE_RAYON,
      vasqueFond: CHUTE_FOND,
      // La chute de l'ile 1 tombe dans la mer, comme elle l'a toujours fait:
      // aucune hauteur ne change, donc le terrain reste identique au bit pres.
      hBassin: SEA_LEVEL,
      longAmont: CHUTE_LONG_AMONT,
      lit,
    }
  }

  /** Point le plus bas d'une section de lit, en travers du courant. */
  private pointBasLit(x: number, z: number, px: number, pz: number): number {
    let bas = Infinity
    for (let k = -2; k <= 2; k++) {
      const n = (k * CHUTE_DEMI_LIT) / 2
      const h = this.brute(x + px * n, z + pz * n)
      if (h < bas) bas = h
    }
    return bas
  }

  /**
   * Ilots au large: tires au hasard, gardes seulement s'ils tombent VRAIMENT
   * dans l'eau (au moins 7 m au large du rivage) et assez loin du bord de la
   * carte pour ne pas etre coupes. Les grandes baies du littoral leur laissent
   * la place; les fonds de baie en recoivent donc naturellement plus.
   */
  private semerIlots(a: Alea): void {
    for (let essai = 0; essai < ILOTS_ESSAIS && this.ilots.length < ILOTS_VOULUS; essai++) {
      const x = a.entre(8, WORLD_SIZE - 8)
      const z = a.entre(8, WORLD_SIZE - 8)
      const r = a.entre(3.5, 10)
      const cime = a.entre(0.9, 5.4)
      const graine = (this.seed + 9400 + essai * 17) | 0
      const d = this.distanceCote(x, z)
      if (d > -4.5 || d < -64) continue
      let libre = true
      for (const o of this.ilots) {
        if (dist(x, z, o.x, o.z) < 20) {
          libre = false
          break
        }
      }
      if (!libre) continue
      this.ilots.push({ x, z, r, cime, graine })
    }
  }

  /**
   * Un cours d'eau: son axe est tabule au metre le long du cours.
   *
   * La sinuosite est ramenee a zero a la source pour que la riviere sorte
   * vraiment du lac et non a cote.
   */
  private faireRiviere(
    sens: 'ouest' | 'sud',
    depart: number,
    base: number,
    arrivee: number,
    graine: number,
  ): Riviere {
    const centre = new Float64Array(WORLD_SIZE + 1)
    for (let i = 0; i <= WORLD_SIZE; i++) {
      const t = clamp((i - depart) / (arrivee - depart), 0, 1)
      centre[i] = base + (fbm(i * 0.011, 7.5, graine, 2) - 0.5) * 74 * smoothstep(0, 0.3, t)
    }
    return { sens, depart, arrivee, centre }
  }

  /** Hauteur analytique, avant aplanissement du centre. */
  private brute(x: number, z: number): number {
    calculerPoids(x, z, this.seed, this.tampon)
    return this.assembler(x, z, this.tampon)
  }

  private assembler(x: number, z: number, p: Float64Array): number {
    const seed = this.seed
    let h = 0
    const wPlage = p[0] ?? 0
    const wForet = p[1] ?? 0
    const wMontagne = p[2] ?? 0
    const wGlacier = p[3] ?? 0
    const wDesert = p[4] ?? 0
    if (wPlage > POIDS_MIN) h += wPlage * hPlage(x, z, seed)
    if (wForet > POIDS_MIN) h += wForet * hForet(x, z, seed)
    if (wMontagne > POIDS_MIN) h += wMontagne * hMontagne(x, z, seed)
    if (wGlacier > POIDS_MIN) h += wGlacier * hGlacier(x, z, seed)
    if (wDesert > POIDS_MIN) h += wDesert * hDesert(x, z, seed)

    h = this.littoral(x, z, h, p)
    h = this.hydrographie(x, z, h)
    return noyerBordure(x, z, h)
  }

  /**
   * Distance signee au trait de cote: positive dans les terres, negative en mer.
   * Les quatre bords sont reunis par un minimum adouci.
   *
   * Les quatre distances par bord restent dans `this.cotes`: `forceCrique` en a
   * besoin juste apres pour savoir QUEL bord gouverne ici, et les recalculer
   * doublerait le nombre de lectures de table du poste le plus chaud du
   * generateur.
   */
  private distanceCote(x: number, z: number): number {
    const t = tablesPour(this.seed)
    const c = this.cotes
    const dO = x - lireTable(t.cOuest, z)
    const dE = WORLD_SIZE - x - lireTable(t.cEst, z)
    const dN = z - lireTable(t.cNord, x)
    const dS = WORLD_SIZE - z - lireTable(t.cSud, x)
    c[0] = dO
    c[1] = dE
    c[2] = dN
    c[3] = dS
    return minAdouci(minAdouci(dO, dE, ADOUCI_COTE), minAdouci(dN, dS, ADOUCI_COTE), ADOUCI_COTE)
  }

  /**
   * Force de la crique en (x, z), dans [0, 1]. A appeler juste apres
   * `distanceCote`, dont elle relit les quatre distances par bord.
   *
   * Chaque bord n'est consulte que s'il GOUVERNE le rivage ici, c'est-a-dire si
   * sa distance est celle qui a gagne le minimum adouci. Sans ce filtre, la table
   * du nord (indexee par x) s'appliquerait aussi a la cote ouest, ou x est
   * constant: le bord ouest tout entier serait alors une seule crique, ou aucune.
   * Pres d'un coin les deux bords voisins comptent tous les deux, et le cap y
   * recoit donc les criques des deux tables.
   */
  private forceCrique(x: number, z: number, d: number): number {
    const t = tablesPour(this.seed)
    const c = this.cotes
    let f = 0
    for (let i = 0; i < 4; i++) {
      const gouverne = smoothstep(GOUVERNE_COTE, 4, (c[i] ?? 0) - d)
      if (gouverne <= 0) continue
      const table = i === 0 ? t.kOuest : i === 1 ? t.kEst : i === 2 ? t.kNord : t.kSud
      const v = lireTable(table, i < 2 ? z : x) * gouverne
      if (v > f) f = v
    }
    return f
  }

  /**
   * Rencontre du relief et de la mer.
   *
   * Le relief est ramene vers l'altitude de greve sur la largeur du biome, puis
   * plonge vers le fond marin. Le plateau oceanique est applique APRES le
   * melange des biomes: le rivage reste ainsi continu meme dans les coins ou
   * plage, glacier et desert se rencontrent.
   */
  private littoral(x: number, z: number, relief: number, p: Float64Array): number {
    const seed = this.seed
    const d = this.distanceCote(x, z)

    let greve = 0
    let largeur = 0
    let chute = 0
    for (let i = 0; i < 5; i++) {
      const w = p[i] ?? 0
      if (w <= 0) continue
      greve += w * (GREVE[i] ?? 2)
      largeur += w * (LARGEUR_COTE[i] ?? 50)
      chute += w * (CHUTE_COTE[i] ?? 10)
    }

    // Crique. Deux effets, et il faut les deux.
    //
    // Le PROFIL du biome se fond vers celui d'une greve douce: la greve descend
    // au ras de l'eau — le trait d'eau se pose alors sur le palier du profil, ou
    // la pente est nulle des deux cotes, au lieu du milieu du plongeon — et le
    // plongeon s'etale. La largeur, elle, n'est jamais reduite: la plage et le
    // desert gardent leur grand adoucissement, les cotes raides gagnent de quoi
    // remonter a pied depuis l'anse.
    //
    // Et le RIVAGE recule, ce que le profil seul ne saurait pas faire: voir
    // RETRAIT_CRIQUE. Le tout est eteint avant FONDU_CRIQUE metres dans les
    // terres — la plage adoucit son relief sur 66 m, et sans cette extinction
    // abaisser sa greve de quelques dizaines de centimetres se lisait encore a
    // 69 m du rivage, donc peut-etre sous une maison deja batie.
    const criqueBrute = this.forceCrique(x, z, d)
    const fondu = smoothstep(FONDU_CRIQUE, 6, d)
    const k = criqueBrute * fondu
    if (k > 0) {
      greve = lerp(greve, GREVE_CRIQUE, k)
      chute = lerp(chute, CHUTE_CRIQUE, k)
      if (largeur < LARGEUR_CRIQUE) largeur = lerp(largeur, LARGEUR_CRIQUE, k)
    }
    // Retrait local du trait de cote par rapport au bord de la carte: c'est lui
    // qui dit si le recul de la crique sert a quelque chose ici. L'avancee qui le
    // compense, elle, s'applique partout.
    const inset = Math.min(x, z, WORLD_SIZE - x, WORLD_SIZE - z) - d
    const utile = smoothstep(RIVAGE_LARGE, 6, inset)
    const dc = d - RETRAIT_CRIQUE * (criqueBrute * utile - tablesPour(seed).moyCrique) * fondu

    // La greve ondule: sans cela le trait de cote serait une courbe trop propre.
    // Dans une crique l'ondulation est etouffee, sinon elle reinstalle le trait
    // d'eau au milieu du plongeon et rend la sortie a nouveau infranchissable.
    greve += (fbm(x * 0.055, z * 0.055, seed + 4441, 2) - 0.5) * 2.2 * (1 - 0.7 * k)

    let h = dc >= largeur ? relief : lerp(greve, relief, smoothstep(0, largeur, dc))
    if (dc >= 0) return h

    const fond = -3 - 8.5 * smoothstep(0, 60, -dc) + (fbm(x * 0.04, z * 0.04, seed + 4442, 2) - 0.5) * 2.4
    h = lerp(greve, fond, smoothstep(0, -chute, dc))

    // Ecueils: des cretes de bruit qui affleurent le long du rivage. Les vraies
    // iles, elles, sont semees une par une (voir `semerIlots`).
    // Une crique est une passe: elle en est nettoyee, sans quoi l'ecueil devant
    // elle redevient la premiere terre rencontree, et une terre imprenable.
    const large = smoothstep(-5, -13, dc) * (1 - k)
    if (large > 0) {
      const bosse = fbmCrete(x * 0.052, z * 0.052, seed + 4443, 2)
      const force = smoothstep(0.66, 0.93, bosse) * large
      if (force > 0) {
        const rocheux = (p[2] ?? 0) * 0.7 + (p[3] ?? 0) * 0.3
        h = Math.max(h, -1.4 + force * (2.4 + 4.2 * rocheux))
      }
    }

    // Banquise: devant la falaise de glace du nord, des plaques plates flottent
    // au ras de l'eau. Elles ont besoin d'eau libre, donc c'est surtout dans les
    // golfes du nord — les seuls endroits assez larges — qu'on les voit.
    const wGlacier = p[3] ?? 0
    if (wGlacier > 0.3) {
      const plaque = fbm(x * 0.05, z * 0.05, seed + 4444, 2)
      const ban = smoothstep(0.48, 0.6, plaque) * smoothstep(0.3, 0.55, wGlacier) * smoothstep(-2.5, -9, dc) * (1 - k)
      if (ban > 0) h = lerp(h, 0.32, ban)
    }
    return h
  }

  /**
   * Lacs et rivieres, creuses dans le relief deja forme.
   *
   * On ne creuse QUE vers le bas (`Math.min`): un lac ne peut donc jamais
   * fabriquer une bosse, et une riviere qui arrive a la mer se fond dans
   * l'estuaire au lieu d'y batir une digue.
   *
   * Les BERGES sont larges a dessein, et leur profil REMONTE JUSQU'AU RELIEF
   * LOCAL: `hRiv` interpole entre le fond du lit et la hauteur du terrain
   * traverse. Deux consequences. D'abord il n'y a jamais de marche au raccord,
   * quelle que soit la hauteur du pays travers. Ensuite un lit a -0,5 m dans une
   * foret a +13 m demande trente metres pour remonter: sur dix, c'etait une
   * falaise, et deux rivieres suffisaient a couper le monde en quatre.
   */
  private hydrographie(x: number, z: number, depart: number): number {
    let h = depart
    for (const l of this.lacs) {
      const dx = x - l.x
      const dz = z - l.z
      const dd2 = dx * dx + dz * dz
      const portee = l.r * 5.6
      if (dd2 > portee * portee) continue
      const rr = l.r * (0.74 + 0.52 * fbm(x * 0.055, z * 0.055, l.graine, 2))
      const t = Math.sqrt(dd2) / rr
      const fond = -3
      const denivele = h - fond
      // Plus la rive est haute, plus la berge est etalee: un lac au pied d'un
      // plateau garde ainsi des bords ou l'on peut marcher.
      const etale = clamp(denivele / 15, 0.6, 2.1)
      if (t >= 2.6 * etale) continue
      const hLac = fond + denivele * smoothstep(0.55, 2.6 * etale, t)
      if (hLac < h) h = hLac
    }

    for (const r of this.rivieres) {
      const u = r.sens === 'ouest' ? x : z
      const v = r.sens === 'ouest' ? z : x
      const t = (u - r.depart) / (r.arrivee - r.depart)
      if (t < 0 || t > 1.02) continue
      const w = 4.5 + 3 * t
      const ecart = Math.abs(v - lireTable(r.centre, u)) / w
      if (ecart >= 12) continue
      // Le lit descend vers la mer: -0,4 m a la source, -1,9 m a l'embouchure.
      const lit = -0.4 - 1.5 * t
      const denivele = h - lit
      const etale = clamp(denivele / 13, 0.55, 2.1)
      if (ecart >= 5.6 * etale) continue
      const hRiv = lit + denivele * smoothstep(0.55 * etale, 5.6 * etale, ecart)
      // La source se fond dans le lac: pas de trou au depart du cours.
      h = lerp(h, hRiv < h ? hRiv : h, smoothstep(0, 0.1, t))
    }

    h = this.sculpterChute(x, z, h)

    for (const o of this.ilots) {
      const dx = x - o.x
      const dz = z - o.z
      const dd2 = dx * dx + dz * dz
      const portee = o.r * 1.6
      if (dd2 > portee * portee) continue
      const rr = o.r * (0.62 + 0.66 * fbm(x * 0.115, z * 0.115, o.graine, 2))
      const t = Math.sqrt(dd2) / rr
      if (t >= 1.35) continue
      // Le grain casse le dome: un ilot n'est pas un chapeau de sable.
      const grain = 0.78 + 0.44 * fbm(x * 0.21, z * 0.21, o.graine + 5, 2)
      h = Math.max(h, -1.5 + (o.cime + 1.5) * (1 - smoothstep(0, 1.15, t)) * grain)
    }
    return h
  }

  /**
   * Le lit, la falaise et la vasque, tailles dans le relief deja forme.
   *
   * Comme les lacs et les rivieres, on ne creuse QUE vers le bas: la chute ne
   * peut donc jamais fabriquer une bosse, et chaque piece s'eteint exactement
   * la ou elle rejoint le terrain d'origine — c'est ce qui borne son emprise.
   *
   * La FALAISE n'est pas taillee: c'est la berge amont de la vasque, reduite a
   * trois metres. Le lit s'arrete la ou elle passe sous lui, et la crete tombe
   * donc pile au bon endroit sans qu'on ait a la placer.
   */
  private sculpterChute(x: number, z: number, h: number): number {
    const c = this.chuteChoisie
    if (!c) return h
    const px = x - c.x
    const pz = z - c.z
    if (px * px + pz * pz > CHUTE_PORTEE * CHUTE_PORTEE) return h
    const s = px * c.dx + pz * c.dz
    const n = Math.abs(px * c.dz - pz * c.dx)

    // Le lit, en amont de la bascule. Il DEBORDE un peu au-dela, sinon les
    // sommets de la grille tombes juste apres la bascule gardent leur hauteur
    // d'origine et la crete se retrouve dentelee d'un metre et demi.
    if (s <= CHUTE_DEBORD && s > -c.longAmont) {
      const remont = Math.max(0, -s)
      const i = Math.floor(remont)
      const a = c.lit[i] ?? c.hCrete
      const b = c.lit[i + 1] ?? a
      const lit = a + (b - a) * (remont - i)
      const denivele = h - lit
      if (denivele > 0) {
        const berge = clamp(denivele * CHUTE_BERGE, CHUTE_BERGE_LIT_MIN, CHUTE_BERGE_LIT_MAX)
        const large = c.demiLit + berge
        if (n < large) {
          let hl = lit + denivele * smoothstep(c.demiLit, large, n)
          hl = lerp(h, hl, smoothstep(c.longAmont, c.longAmont - CHUTE_FONDU_AMONT, remont))
          if (hl < h) h = hl
        }
      }
    }

    // La vasque. Elle est creusee SOUS le niveau de la mer: la nappe la remplit
    // toute seule, on y nage, et sa berge aval rejoint celle du lac.
    const vx = x - c.vasqueX
    const vz = z - c.vasqueZ
    const dd = Math.sqrt(vx * vx + vz * vz)
    const denivele = h - c.vasqueFond
    if (denivele > 0 && dd < c.vasqueR + CHUTE_BERGE_MAX) {
      // Plein amont, la berge devient la falaise; partout ailleurs elle
      // s'etale d'autant plus que le pays est haut.
      const versAmont = dd > 1e-6 ? -(vx * c.dx + vz * c.dz) / dd : 1
      const douce = clamp(denivele * CHUTE_BERGE, 6, CHUTE_BERGE_MAX)
      const mur = Math.max(
        smoothstep(-0.55, 0.85, versAmont),
        smoothstep(CHUTE_BERGE_MAX / CHUTE_BERGE, CHUTE_BERGE_MAX / CHUTE_BERGE + CHUTE_BERGE_RENONCE, denivele),
      )
      const berge = lerp(douce, CHUTE_MUR, mur)
      const bord = c.vasqueR + berge
      if (dd < bord) {
        const hv = c.vasqueFond + denivele * smoothstep(c.vasqueR, bord, dd)
        if (hv < h) h = hv
      }
    }
    return h
  }

  private remplir(): void {
    const p = this.tampon
    for (let iz = 0; iz < N; iz++) {
      const z = iz * TERRAIN_CELL
      for (let ix = 0; ix < N; ix++) {
        const x = ix * TERRAIN_CELL
        calculerPoids(x, z, this.seed, p)
        let h = this.assembler(x, z, p)

        const d = dist(x, z, CENTRE, CENTRE)
        if (d < RAYON_FONDU) h = lerp(this.hCentre, h, smoothstep(RAYON_PLAT, RAYON_FONDU, d))

        let meilleur = 0
        let meilleurPoids = -1
        for (let b = 0; b < 5; b++) {
          const w = p[b] ?? 0
          if (w > meilleurPoids) {
            meilleurPoids = w
            meilleur = b
          }
        }
        const i = iz * N + ix
        this.hauteurs[i] = h
        this.biomes[i] = meilleur
      }
    }
  }

  // ---------------------------------------------------------------- lectures

  private cell(i: number): number {
    return this.hauteurs[i] ?? SEA_LEVEL
  }

  hauteur(x: number, z: number): number {
    if (!(x >= 0 && z >= 0 && x <= WORLD_SIZE && z <= WORLD_SIZE)) return SEA_LEVEL
    const gx = x / TERRAIN_CELL
    const gz = z / TERRAIN_CELL
    let ix = Math.floor(gx)
    let iz = Math.floor(gz)
    if (ix > N - 2) ix = N - 2
    if (iz > N - 2) iz = N - 2
    const fx = gx - ix
    const fz = gz - iz
    const i = iz * N + ix
    const a = this.cell(i)
    const b = this.cell(i + 1)
    const c = this.cell(i + N)
    const d = this.cell(i + N + 1)
    return lerp(lerp(a, b, fx), lerp(c, d, fx), fz)
  }

  biome(x: number, z: number): BiomeId {
    const ix = clamp(Math.round(x / TERRAIN_CELL), 0, N - 1)
    const iz = clamp(Math.round(z / TERRAIN_CELL), 0, N - 1)
    const b = this.biomes[iz * N + ix] ?? 1
    return ORDRE[b] ?? 'foret'
  }

  pente(x: number, z: number): number {
    const d = TERRAIN_CELL
    const xg = clamp(x - d, 0, WORLD_SIZE)
    const xd = clamp(x + d, 0, WORLD_SIZE)
    const zn = clamp(z - d, 0, WORLD_SIZE)
    const zs = clamp(z + d, 0, WORLD_SIZE)
    const gx = (this.hauteur(xd, z) - this.hauteur(xg, z)) / Math.max(1e-6, xd - xg)
    const gz = (this.hauteur(x, zs) - this.hauteur(x, zn)) / Math.max(1e-6, zs - zn)
    return Math.sqrt(gx * gx + gz * gz)
  }

  estEau(x: number, z: number): boolean {
    return this.hauteur(x, z) < SEA_LEVEL
  }

  echantillon(x: number, z: number): TerrainSample {
    const hauteur = this.hauteur(x, z)
    const biome = this.biome(x, z)
    const pente = this.pente(x, z)
    return { hauteur, biome, pente, sol: solDe(biome, hauteur, pente) }
  }

  /** Normale du sol, deduite du gradient. Utile au rendu et aux decors. */
  normale(x: number, z: number, sortie: [number, number, number]): void {
    const d = TERRAIN_CELL
    const gx = (this.hauteur(clamp(x + d, 0, WORLD_SIZE), z) - this.hauteur(clamp(x - d, 0, WORLD_SIZE), z)) / (2 * d)
    const gz = (this.hauteur(x, clamp(z + d, 0, WORLD_SIZE)) - this.hauteur(x, clamp(z - d, 0, WORLD_SIZE))) / (2 * d)
    const inv = 1 / Math.sqrt(gx * gx + gz * gz + 1)
    sortie[0] = -gx * inv
    sortie[1] = inv
    sortie[2] = -gz * inv
  }

  /** Vrai si le point est dans l'emprise de cette ile. */
  contient(x: number, z: number): boolean {
    return x >= 0 && z >= 0 && x <= WORLD_SIZE && z <= WORLD_SIZE
  }

  /**
   * La teinte du sol de cette ile. C'est une METHODE parce que chaque ile
   * repond pour elle-meme: la fonction libre ci-dessous deduit la couleur de la
   * position dans la disposition des cinq biomes de CETTE ile, et n'a donc
   * aucun sens a mille metres de la.
   */
  couleurSol(x: number, z: number): number {
    return couleurSol(x, z, this)
  }

  /**
   * Hauteur minimale et maximale sur un rectangle du monde.
   *
   * C'est un MINIMUM EXACT sur la grille au metre, pas un test des quatre
   * coins: la lecon de la piste d'aeroport, « un sommet ne dit la hauteur du
   * sol qu'EN UN POINT ». Le rendu de l'eau s'en sert pour savoir si une maille
   * est entierement seche, et une riviere de trois metres au milieu d'une
   * maille de quatre ferait sinon un trou dans la mer.
   */
  bornes(x0: number, x1: number, z0: number, z1: number): { min: number; max: number } {
    const ia = clamp(Math.floor(x0 / TERRAIN_CELL), 0, N - 1)
    const ib = clamp(Math.ceil(x1 / TERRAIN_CELL), 0, N - 1)
    const ja = clamp(Math.floor(z0 / TERRAIN_CELL), 0, N - 1)
    const jb = clamp(Math.ceil(z1 / TERRAIN_CELL), 0, N - 1)
    let min = Infinity
    let max = -Infinity
    for (let iz = ja; iz <= jb; iz++) {
      for (let ix = ia; ix <= ib; ix++) {
        const h = this.cell(iz * N + ix)
        if (h < min) min = h
        if (h > max) max = h
      }
    }
    if (min === Infinity) return { min: SEA_LEVEL, max: SEA_LEVEL }
    return { min, max }
  }

  /** Hauteur minimale et maximale d'un chunk, pour les spheres englobantes. */
  bornesChunk(cx: number, cz: number): { min: number; max: number } {
    const x = cx * CHUNK_SIZE
    const z = cz * CHUNK_SIZE
    return this.bornes(x, x + CHUNK_SIZE, z, z + CHUNK_SIZE)
  }
}

// ------------------------------------------------------------------ sol

function solDe(b: BiomeId, h: number, pente: number): SolId {
  if (h < SEA_LEVEL) return 'eau'
  if (b === 'montagne') {
    if (h > 38) return 'neige'
    // Les cotes rocheuses de l'est: du sable n'y aurait aucun sens.
    return pente > 0.55 || h < 2.2 ? 'roche' : 'herbe'
  }
  if (b === 'foret') return pente > 0.7 ? 'terre' : 'herbe'
  // Le desert finit en plage: sous 2 m, c'est du sable mouille, deja son sol.
  return BIOMES[b].sol
}

// ------------------------------------------------------------------ couleurs

const SABLE_MOUILLE = 0xa87c48
const SABLE_SEC = 0xf9edc2
const LAISSE_MER = 0xc39a5f
const ECUME_SABLE = 0xffe8d2
const HERBE_ALPINE = 0x66a052
const NEIGE_SOMMET = 0xf4f8ff
const ROCHE_PENTE = 0x7b7488
const ROCHE_VEINE = 0xc0b9cf
const ROCHE_STRATE = 0x9c7558
const ROCHE_MOUILLEE = 0x4a4655
const LICHEN = 0xc2b552
const GLACE_BLEUE = 0x5ec4dd
const GLACE_PLAQUE = 0x5fc6e4
const GLACE_PROFONDE = 0x2f92bd
const OMBRE_NEIGE = 0xb6d5f4
const ETINCELLE_NEIGE = 0xffffff
const ROCHE_DESERT = 0xa8542f
const SABLE_CRETE = 0xfad78d
const GRAVIER_DESERT = 0xbd8f60
const CAILLOUX_DESERT = 0x92694a
const VEINE_DESERT = 0xd9713c
const TERRE_FORET = 0x6b4a2a
const TERRE_NUE = 0x936036
const LITIERE = 0xbe7f38
const LITIERE_CLAIRE = 0xdba85a
const MOUSSE = 0x1a6238
const HERBE_SOLEIL = 0x8fd450
const RACINE = 0x6d4b28
const FOND_MARIN = 0x35786d
const VASE_LAC = 0x3f5a3a
/** Galets laves du lit de la chute, et rocher ruisselant du cirque. */
const GALET_CHUTE = 0xb3b1a4
const ROCHE_CHUTE = 0x8d857a

/**
 * Melangeur de couleur en flottants: la couleur en cours vit dans trois
 * variables de module, ce qui evite d'emballer et de deballer un entier a
 * chaque etape. `couleurSol` empile une dizaine de melanges par sommet et est
 * appele 260 000 fois: le gain compte.
 */
let mr = 0
let mg = 0
let mb = 0

function poser(c: number): void {
  mr = (c >> 16) & 255
  mg = (c >> 8) & 255
  mb = c & 255
}

function meler(c: number, t: number): void {
  if (t <= 0) return
  const u = t < 1 ? t : 1
  mr += (((c >> 16) & 255) - mr) * u
  mg += (((c >> 8) & 255) - mg) * u
  mb += ((c & 255) - mb) * u
}

/** Tampon des poids de biome, reutilise par couleurSol. */
const poidsCouleur = new Float64Array(5)

/** Onde triangulaire dans [0, 1]: sert aux bandes suivant les isohypses. */
function bande(v: number): number {
  const f = v - Math.floor(v)
  return f < 0.5 ? f * 2 : 2 - f * 2
}

/**
 * Couleur du sol en (x, z), melangee entre biomes voisins puis variee par
 * plusieurs echelles de bruit. C'est ce qui donne au monde sa richesse de
 * teintes sans aucune texture.
 *
 * Cinq champs de bruit sont partages par les cinq biomes, chacun avec un role
 * different, et sont donc calcules une seule fois par sommet:
 *   `tache`  ~22 m et 90 m: les grandes zones, la teinte dominante du coin
 *   `grain`  ~7 m et 4 m: les plaques (litiere, gravier, glace, lichen)
 *   `fin`    ~2,8 m: le piquete (cailloux, coquillages, etincelles)
 *   `veine`  bandes sinueuses ~13 m: racines, filons de roche, crevasses
 *   `pointille` un tirage par sommet: le mouchete qui casse les aplats
 * `pointille` est echantillonne sur la maille de 1 m, donc exactement a la
 * resolution du maillage: c'est la plus fine variation que le sol puisse
 * porter, et elle sert a la fois de teinte et de luminosite.
 * La convexite (bosse ou creux) vient du champ de hauteur, pas du bruit: la
 * mousse tombe donc vraiment dans les creux et l'herbe claire sur les bosses.
 *
 * `littoral` vaut 1 au bord de l'eau et 0 a trois metres au-dessus: c'est lui
 * qui donne a chaque biome sa maniere de toucher la mer, du sable mouille du
 * desert a la glace bleue de la falaise du nord.
 */
export function couleurSol(x: number, z: number, terrain: Terrain): number {
  const seed = terrain.seed
  const p = poidsCouleur
  calculerPoids(x, z, seed, p)

  const h = terrain.hauteur(x, z)
  const pente = terrain.pente(x, z)
  const tache = clamp(
    fbm(x * 0.045, z * 0.045, seed + 6101, 3) * 1.8 - 0.4 + (fbm(x * 0.011, z * 0.011, seed + 6103, 2) - 0.5) * 0.55,
    0,
    1,
  )
  const grain = fbm(x * 0.135, z * 0.135, seed + 6104, 2)
  const fin = bruitValeur(x * 0.36, z * 0.36, seed + 6105)
  // Les bandes sinueuses sont echantillonnees sur un domaine tourne de 31
  // degres ET deforme par les deux champs precedents: sans cela, les plis du
  // bruit de valeur se rangent sur la grille du monde et les racines
  // ressemblent a un circuit imprime.
  const vx = (x * 0.857 - z * 0.516) * 0.078 + tache * 2.4
  const vz = (x * 0.516 + z * 0.857) * 0.078 + grain * 2.4
  const veine = 1 - Math.abs(bruitValeur(vx, vz, seed + 6106) * 2 - 1)
  const pointille = bruitValeur(x, z, seed + 6102)
  // Ecart signe du mouchete, dans [-1, 1].
  const moucheEcart = pointille * 2 - 1

  // Convexite locale: 0 dans un creux, 1 sur une bosse.
  const xg = clamp(x - 3.5, 0, WORLD_SIZE)
  const xd = clamp(x + 3.5, 0, WORLD_SIZE)
  const zn = clamp(z - 3.5, 0, WORLD_SIZE)
  const zs = clamp(z + 3.5, 0, WORLD_SIZE)
  const voisin = (terrain.hauteur(xg, z) + terrain.hauteur(xd, z) + terrain.hauteur(x, zn) + terrain.hauteur(x, zs)) * 0.25
  const convexe = smoothstep(-1.4, 1.4, h - voisin)

  // Proximite du niveau de la mer: 1 au bord de l'eau, 0 a 3,2 m au-dessus.
  const littoral = smoothstep(3.2, -0.2, h)

  let r = 0
  let g = 0
  let b = 0
  for (let i = 0; i < 5; i++) {
    const w = p[i] ?? 0
    if (w <= POIDS_MIN) continue
    const id = ORDRE[i] ?? 'foret'
    const def = BIOMES[id]
    poser(melangerCouleur(def.couleurSol, def.couleurSolVariante, tache))

    if (id === 'plage') {
      // Sable sec et clair sur les dunes, laisse de mer en bandes qui suivent
      // les isohypses, coquillages ecrases en piquete.
      meler(SABLE_SEC, convexe * 0.55)
      // Laisses de mer: des bandes qui suivent les isohypses, donc paralleles
      // au rivage, brouillees par le grain pour qu'elles ne soient pas peignees.
      const marees = smoothstep(0.42, 0.96, bande(h * 2.4 + grain * 0.9))
      const mouille = smoothstep(3.4, 0.05, h)
      meler(LAISSE_MER, mouille * marees)
      meler(SABLE_MOUILLE, mouille * mouille * 0.8)
      meler(ECUME_SABLE, smoothstep(0.79, 0.96, fin) * 0.45)
      meler(moucheEcart > 0 ? ECUME_SABLE : LAISSE_MER, Math.abs(moucheEcart) * 0.16)
    } else if (id === 'montagne') {
      // Le pied du massif est un vrai alpage: sans ca, le melange roche + herbe
      // donne un vert-gris de brume qui a l'air sale.
      const herbeux = smoothstep(19, 5, h) * (1 - smoothstep(0.5, 0.95, pente)) * (1 - littoral)
      meler(HERBE_ALPINE, herbeux * 0.95)
      meler(HERBE_SOLEIL, herbeux * smoothstep(0.45, 0.88, grain) * 0.5)
      meler(MOUSSE, herbeux * smoothstep(0.5, 0.14, grain) * 0.42)
      // Lichen jaune sur les replats: c'est ce qui garde la montagne joyeuse.
      meler(LICHEN, smoothstep(0.6, 0.9, grain) * smoothstep(0.75, 0.3, pente) * 0.5 * (1 - littoral))
      meler(ROCHE_PENTE, smoothstep(0.55, 1.15, pente))
      // Strates ocres et filons clairs, visibles surtout sur les faces raides.
      const raide = smoothstep(0.35, 0.9, pente)
      meler(ROCHE_STRATE, smoothstep(0.35, 0.95, bande(h * 0.19 + tache * 0.6)) * raide * 0.45)
      meler(ROCHE_VEINE, smoothstep(0.78, 1, veine) * (0.3 + 0.55 * raide))
      meler(moucheEcart > 0 ? ROCHE_VEINE : ROCHE_STRATE, Math.abs(moucheEcart) * 0.15 * (1 - herbeux))
      meler(moucheEcart > 0 ? HERBE_SOLEIL : MOUSSE, Math.abs(moucheEcart) * 0.17 * herbeux)
      meler(NEIGE_SOMMET, smoothstep(34, 48, h))
      // Estran rocheux: la roche battue par la mer est sombre et luisante.
      meler(ROCHE_PENTE, littoral * 0.7)
      meler(ROCHE_MOUILLEE, littoral * littoral * 0.75)
    } else if (id === 'glacier') {
      // Neige bleutee dans les creux, plaques de glace turquoise, crevasses.
      meler(OMBRE_NEIGE, (1 - convexe) * 0.6)
      meler(GLACE_PLAQUE, smoothstep(0.57, 0.83, grain) * 0.72)
      meler(GLACE_PROFONDE, smoothstep(0.85, 1, veine) * 0.65)
      meler(GLACE_BLEUE, smoothstep(0.25, 0.75, pente) * 0.55)
      meler(ETINCELLE_NEIGE, convexe * smoothstep(0.78, 0.97, fin) * 0.5)
      meler(moucheEcart > 0 ? ETINCELLE_NEIGE : OMBRE_NEIGE, Math.abs(moucheEcart) * 0.14)
      // Falaise de glace: la tranche est bleue, et d'autant plus qu'elle est
      // raide. C'est ce qui fait lire le nord comme un front glaciaire.
      meler(GLACE_BLEUE, littoral * smoothstep(0.6, 1.6, pente) * 0.85)
      meler(GLACE_PROFONDE, littoral * littoral * 0.5)
    } else if (id === 'desert') {
      meler(SABLE_CRETE, convexe * 0.55)
      // Reg: plaques de gravier plus grises, semees de cailloux.
      const caillouteux = smoothstep(0.5, 0.76, grain * 0.65 + tache * 0.35) * (1 - littoral)
      meler(GRAVIER_DESERT, caillouteux * 0.9)
      meler(CAILLOUX_DESERT, caillouteux * smoothstep(0.58, 0.9, fin) * 0.85)
      meler(ROCHE_DESERT, smoothstep(0.3, 0.85, pente) * 0.75 * (1 - littoral))
      meler(VEINE_DESERT, smoothstep(0.86, 1, veine) * 0.45 * (1 - littoral))
      meler(moucheEcart > 0 ? SABLE_CRETE : GRAVIER_DESERT, Math.abs(moucheEcart) * 0.16)
      // Les dunes s'aplatissent en plage: sable clair puis sable mouille.
      meler(SABLE_SEC, littoral * 0.75)
      meler(LAISSE_MER, littoral * smoothstep(0.42, 0.96, bande(h * 2.4 + grain * 0.9)) * 0.8)
      meler(SABLE_MOUILLE, littoral * littoral * 0.8)
      meler(ECUME_SABLE, littoral * smoothstep(0.79, 0.96, fin) * 0.5)
    } else {
      // Foret: mousse dans les creux, herbe ensoleillee sur les bosses, plaques
      // de litiere de feuilles, coeurs de terre nue, racines en surface.
      // L'herbe respire a l'echelle de la clairiere (7 m) avant meme le relief.
      meler(HERBE_SOLEIL, smoothstep(0.44, 0.86, grain) * 0.5)
      meler(MOUSSE, smoothstep(0.56, 0.16, grain) * 0.5)
      meler(MOUSSE, (1 - convexe) * 0.4)
      meler(HERBE_SOLEIL, convexe * 0.36)
      // Un brin d'herbe fait quelques centimetres et le maillage un metre: le
      // mouchete par sommet est la seule texture fine que le sol puisse porter.
      meler(moucheEcart > 0 ? HERBE_SOLEIL : MOUSSE, Math.abs(moucheEcart) * 0.17)
      // Litiere: des plaques de 3 m groupees en tapis de 20 m, comme sous un
      // bouquet d'arbres. `fin` et `tache` sont independants de `grain`, donc
      // les feuilles ne tombent pas systematiquement sur l'herbe claire.
      const litiere = smoothstep(0.46, 0.74, fin * 0.55 + tache * 0.45)
      meler(LITIERE, litiere * 0.78)
      meler(LITIERE_CLAIRE, litiere * litiere * smoothstep(0.45, 0.9, grain) * 0.7)
      meler(TERRE_NUE, smoothstep(0.66, 0.9, fin * 0.6 + tache * 0.4) * 0.75)
      // Les racines courent la ou la litiere s'accumule, pas partout.
      meler(RACINE, smoothstep(0.86, 1, veine) * (0.15 + 0.55 * litiere))
      meler(TERRE_FORET, smoothstep(0.55, 1.05, pente))
      // Berges des lacs et des rivieres: vase et galets, pas de mousse claire.
      meler(VASE_LAC, littoral * 0.65)
    }

    r += w * mr
    g += w * mg
    b += w * mb
  }

  // Les creux sont plus sombres: cela dessine le relief meme a plat.
  let mul = lerp(0.84, 1.08, convexe)
  mul *= 0.962 + pointille * 0.076

  let couleur = (Math.round(clamp(r * mul, 0, 255)) << 16) | (Math.round(clamp(g * mul, 0, 255)) << 8) | Math.round(clamp(b * mul, 0, 255))

  // Autour de la chute, le sol n'est plus de la foret. La litiere de feuilles et
  // l'herbe claire sur une paroi que l'eau lave a longueur de journee auraient
  // eu l'air d'un decor pose par erreur: les parois passent au rocher, le lit
  // aux galets, et le bas du cirque au rocher mouille. C'est local et borne au
  // meme disque que le relief, donc rien ne bouge ailleurs dans le monde.
  const chute = terrain.chute
  if (chute) {
    const dx = x - chute.x
    const dz = z - chute.z
    const d2 = dx * dx + dz * dz
    if (d2 < CHUTE_PORTEE * CHUTE_PORTEE) {
      // Le rocher ne deborde pas: au-dela d'une trentaine de metres la foret
      // reprend ses droits. Un halo gris de cinquante metres de rayon se lisait
      // comme une brulure dans le paysage, pas comme un cirque.
      const proche = smoothstep(CHUTE_PORTEE * 0.86, CHUTE_PORTEE * 0.42, Math.sqrt(d2))
      const paroi = proche * smoothstep(0.55, 1.1, pente)
      couleur = melangerCouleur(couleur, ROCHE_CHUTE, paroi * 0.8)
      couleur = melangerCouleur(couleur, ROCHE_PENTE, paroi * smoothstep(0.42, 0.78, grain) * 0.45)
      // Un cirque de rocher nu et parfaitement uni se lit comme une tache de
      // brulure. La mousse s'accroche dans les fissures, et c'est elle qui le
      // rend a la foret.
      couleur = melangerCouleur(couleur, MOUSSE, paroi * smoothstep(0.58, 0.92, fin) * 0.3)
      // Le lit, taille a plat au ras du courant: des galets, pas de la terre.
      const s = dx * chute.dx + dz * chute.dz
      const n = Math.abs(dx * chute.dz - dz * chute.dx)
      const lit = proche * smoothstep(chute.demiLit + 2.2, chute.demiLit - 1, n) * smoothstep(2, -1, s)
      couleur = melangerCouleur(couleur, GALET_CHUTE, lit * (0.55 + 0.35 * fin))
      // Le rocher du pied reste sombre et luisant: les embruns ne le sechent
      // jamais. Le seuil suit la hauteur, pas la distance: seul le bas mouille.
      couleur = melangerCouleur(couleur, ROCHE_MOUILLEE, proche * smoothstep(4, 0.4, h) * 0.34)
    }
  }
  // Sous l'eau, la couleur du sol se fond vite dans celle du fond marin: le
  // plateau continental hors du monde porte la meme teinte, et le bord de la
  // carte cesse ainsi de se voir comme un changement de couleur sous l'eau.
  if (h < SEA_LEVEL) couleur = melangerCouleur(couleur, FOND_MARIN, smoothstep(0, -2.4, h) * 0.9)
  return couleur
}

// ------------------------------------------------------------------ marche

/**
 * Vrai si (x, z) tombe dans le lit du ruisseau de la chute.
 *
 * Le lit est plat, sec au sens du champ de hauteur et parfaitement marchable:
 * les semeurs d'arbres et de touffes d'herbe le trouvent donc excellent, et la
 * premiere image de la chute montrait un chene plante au beau milieu du
 * courant. On leur donne de quoi l'eviter.
 *
 * Le terrain arrive par l'interface `ITerrain` chez les semeurs, qui n'ont
 * aucune raison de connaitre le generateur: la chute est donc lue par une
 * propriete optionnelle, et l'absence de chute est une reponse valable.
 */
export function dansLeLitDeLaChute(terrain: ITerrain, x: number, z: number): boolean {
  /*
   * UNE OU PLUSIEURS: le monde en a deux depuis que l'ile 2 a sa cascade, et
   * les faux terrains des tests n'en ont aucune. On accepte donc les deux
   * formes — `chutes` d'abord, `chute` ensuite — sans qu'aucun terrain factice
   * ait une ligne a apprendre, ce qui etait deja la raison du typage
   * structurel.
   */
  const t = terrain as { chutes?: readonly Chute[]; chute?: Chute | null }
  const liste = t.chutes ?? (t.chute ? [t.chute] : [])
  for (const c of liste) {
    if (dansCeLit(c, x, z)) return true
  }
  return false
}

/** Vrai si le point est dans le lit de CETTE chute-la. */
function dansCeLit(c: Chute, x: number, z: number): boolean {
  const dx = x - c.x
  const dz = z - c.z
  if (dx * dx + dz * dz > CHUTE_PORTEE * CHUTE_PORTEE) return false
  const s = dx * c.dx + dz * c.dz
  if (s > CHUTE_DEBORD || s < -c.longAmont) return false
  return Math.abs(dx * c.dz - dz * c.dx) < c.demiLit + 1.4
}

/**
 * Vrai si l'on peut se tenir la. Les bornes sont celles de la CARTE, pas de
 * l'ile 1: le decor du monde 2 se cherche a x = 1300, et un test ecrit sur
 * WORLD_SIZE l'aurait refuse sans un mot.
 */
export function estMarchable(terrain: ITerrain, x: number, z: number): boolean {
  if (!(x >= 0 && z >= 0 && x <= CARTE_X && z <= CARTE_Z)) return false
  if (terrain.hauteur(x, z) < SEA_LEVEL) return false
  return terrain.pente(x, z) <= MAX_WALK_SLOPE
}

/** Hauteur d'appui d'un objet: jamais sous le niveau de la mer. */
export function hauteurSecuritaire(terrain: ITerrain, x: number, z: number): number {
  const h = terrain.hauteur(x, z)
  return h < SEA_LEVEL ? SEA_LEVEL : h
}

// ------------------------------------------------------------------ props

/** Teintes de base par type d'accessoire. */
const HERBE_PAR_BIOME: Record<BiomeId, number> = {
  plage: 0xc9c274,
  foret: 0x59b93f,
  montagne: 0x84ae67,
  desert: 0xc9a85a,
  glacier: 0xcfe6ea,
  cristal: 0xa7b0d8,
  jungle: 0x5fc23c,
  ferme: 0x8fc24a,
}

const FLEURS_FORET: readonly number[] = [0xff5f8d, 0xffd93d, 0xb26bff, 0xff8f3f, 0xfff4e0]
const FLEURS_MONTAGNE: readonly number[] = [0xc9a7ff, 0xffe27a, 0xfff0f5, 0x8fd0ff]
const FLEURS_DESERT: readonly number[] = [0xffb347, 0xff6f5e, 0xffe9a8]
const FLEURS_PLAGE: readonly number[] = [0xfff1c1, 0xffc9d9]
const FLEURS_JUNGLE: readonly number[] = [0xff3d6e, 0xffb03a, 0xfff06a, 0xd45bff]
const FLEURS_FERME: readonly number[] = [0xffd93d, 0xffffff, 0xff8fa8, 0xc9e35a]
const FLEURS_CRISTAL: readonly number[] = [0xd7c4ee, 0xa8c8ff, 0xf0e6ff]
const COQUILLAGES: readonly number[] = [0xfff0dd, 0xffd8c4, 0xf6e2b8, 0xffc6c0, 0xe8d5f0]
const CHAMPIGNONS: readonly number[] = [0xd9463c, 0xe8823a, 0xf0e0c0, 0xb8607f]
const GLACONS: readonly number[] = [0xc6ecff, 0xa8dff2, 0xe6f7ff]

/**
 * La palette de fleurs de chaque biome.
 *
 * C'etait une chaine de ternaires qui retombait sur la plage pour tout ce
 * qu'elle ne nommait pas: les trois biomes de l'ile 2 y auraient donc pousse
 * des fleurs de bord de mer sans qu'une ligne le dise. Une table exhaustive ne
 * peut pas oublier un biome — le compilateur s'en charge.
 */
const FLEURS_PAR_BIOME: Record<BiomeId, readonly number[]> = {
  foret: FLEURS_FORET,
  montagne: FLEURS_MONTAGNE,
  desert: FLEURS_DESERT,
  plage: FLEURS_PLAGE,
  glacier: FLEURS_PLAGE,
  jungle: FLEURS_JUNGLE,
  ferme: FLEURS_FERME,
  cristal: FLEURS_CRISTAL,
}

function teintePour(kind: PropKind, biome: BiomeId, a: Alea): number {
  switch (kind) {
    case 'herbe':
      return varier(HERBE_PAR_BIOME[biome], a, 0.14)
    case 'fleur': {
      const palette = FLEURS_PAR_BIOME[biome]
      return varier(a.choix(palette), a, 0.08)
    }
    case 'coquillage':
      return varier(a.choix(COQUILLAGES), a, 0.06)
    case 'champignon':
      return varier(a.choix(CHAMPIGNONS), a, 0.1)
    case 'glacon':
      return varier(a.choix(GLACONS), a, 0.06)
    case 'caillou':
      return varier(melangerCouleur(BIOMES[biome].couleurSol, 0x5a5460, 0.55), a, 0.12)
    default:
      return varier(BIOMES[biome].couleurSol, a, 0.1)
  }
}

/** Fait varier une couleur vers le noir ou le blanc, pour casser l'uniformite. */
function varier(c: number, a: Alea, force: number): number {
  const t = a.entre(-force, force)
  return t < 0 ? melangerCouleur(c, 0x000000, -t) : melangerCouleur(c, 0xffffff, t)
}

/** Echelles (min, max) par type. */
const ECHELLE: Partial<Record<PropKind, [number, number]>> = {
  herbe: [0.8, 1.7],
  fleur: [0.8, 1.45],
  coquillage: [0.65, 1.4],
  champignon: [0.7, 1.5],
  caillou: [0.55, 1.6],
  glacon: [0.6, 1.7],
}

/**
 * Seme les accessoires decoratifs. Les noeuds de ressources recoltables sont
 * generes ailleurs. Un candidat est tire uniformement dans le monde puis
 * accepte avec une probabilite egale au poids du biome: la densite obtenue
 * suit donc exactement la surface reelle du biome, frontieres floues incluses.
 */
export function genererProps(terrain: Terrain, seed: number): Prop[] {
  const a = new Alea((seed ^ 0x5eed_c0de) >>> 0)
  const props: Prop[] = []
  const p = new Float64Array(5)
  const hectares = (WORLD_SIZE * WORLD_SIZE) / 10000
  let id = 1

  for (let ib = 0; ib < ORDRE.length; ib++) {
    const biome = ORDRE[ib]
    if (!biome) continue
    const def = BIOMES[biome]
    const kinds = Object.keys(def.props) as PropKind[]
    let densiteTotale = 0
    for (const k of kinds) densiteTotale += def.props[k] ?? 0
    if (densiteTotale <= 0) continue

    const candidats = Math.round(densiteTotale * hectares)
    for (let c = 0; c < candidats; c++) {
      const x = a.entre(1, WORLD_SIZE - 1)
      const z = a.entre(1, WORLD_SIZE - 1)
      calculerPoids(x, z, seed, p)
      // Le bruit de touffe cree des tapis denses et des clairieres au lieu d'un
      // semis regulier; sa moyenne vaut 1 donc la densite globale est conservee.
      const touffe = clamp(0.28 + 1.45 * fbm(x * 0.035, z * 0.035, seed + 7777, 3), 0, 1.9)
      if (a.reel() >= (p[ib] ?? 0) * touffe) continue
      if (!estMarchable(terrain, x, z)) continue
      if (dansLeLitDeLaChute(terrain, x, z)) continue

      // Choix du type proportionnel a sa densite dans ce biome.
      let tirage = a.reel() * densiteTotale
      let kind: PropKind | undefined
      for (const k of kinds) {
        tirage -= def.props[k] ?? 0
        if (tirage <= 0) {
          kind = k
          break
        }
      }
      if (!kind) kind = kinds[kinds.length - 1]
      if (!kind) continue

      const y = hauteurSecuritaire(terrain, x, z)
      // Les coquillages restent sur la laisse de mer.
      if (kind === 'coquillage' && y > 3) continue
      // Herbe et fleurs ne poussent pas sur la roche nue.
      if ((kind === 'herbe' || kind === 'fleur') && terrain.pente(x, z) > 0.6) continue

      const bornes: [number, number] = ECHELLE[kind] ?? [0.8, 1.2]
      props.push({
        id: id++,
        kind,
        x,
        z,
        y,
        rot: a.entre(0, Math.PI * 2),
        scale: a.entre(bornes[0], bornes[1]),
        teinte: teintePour(kind, biome, a),
      })
    }
  }

  return props
}

/**
 * LE DECOR DE L'ILE 2 — le meme semis, region par region.
 *
 * On ne generalise pas `genererProps`: elle lit `calculerPoids`, c'est-a-dire
 * la disposition des cinq biomes de l'ile 1, et la faire dependre d'une
 * interface pour l'ile 2 aurait change le flux d'aleatoire du monde 1. Une ile
 * qui ne bouge pas d'un bit est une propriete plus precieuse que quelques
 * lignes economisees.
 *
 * Elle prend sa propre graine derivee et ses propres identifiants, plus haut
 * que ceux du monde 1: un accessoire n'est jamais persiste (rien ne le
 * sauvegarde, rien ne s'y refere), donc son identifiant n'a qu'a etre unique
 * dans une partie — l'invariant severe des index d'insertion ne concerne que
 * les noeuds de ressource et les animaux.
 */
export function genererPropsIle2(ile: Ile2Semeur, seed: number, idDepart = 1_000_000): Prop[] {
  const a = new Alea((seed ^ 0x5eed_1e02) >>> 0)
  const props: Prop[] = []
  const hectares = (ile.cote * ile.cote) / 10000
  let id = idDepart

  for (const def of ile.regions()) {
    const kinds = Object.keys(def.props) as PropKind[]
    let densiteTotale = 0
    for (const k of kinds) densiteTotale += def.props[k] ?? 0
    if (densiteTotale <= 0) continue

    // La densite est par hectare de REGION; on tire sur toute l'ile et l'on
    // rejette ce qui tombe ailleurs, exactement comme l'ile 1 tire sur tout le
    // monde et rejette selon le poids du biome. La surface reelle de la region
    // fait donc le compte toute seule.
    const candidats = Math.round(densiteTotale * hectares)
    for (let c = 0; c < candidats; c++) {
      const x = a.entre(ile.x0 + 1, ile.x0 + ile.cote - 1)
      const z = a.entre(ile.z0 + 1, ile.z0 + ile.cote - 1)
      const touffe = clamp(0.28 + 1.45 * fbm(x * 0.035, z * 0.035, seed + 7777, 3), 0, 1.9)
      if (a.reel() >= touffe) continue
      if (ile.regionAuPoint(x, z)?.id !== def.id) continue
      if (!estMarchable(ile, x, z)) continue
      // Le lit de la cascade est plat et marchable: sans cette exception,
      // l'herbe pousse au milieu du courant.
      if (dansLeLitDeLaChute(ile, x, z)) continue

      let tirage = a.reel() * densiteTotale
      let kind: PropKind | undefined
      for (const k of kinds) {
        tirage -= def.props[k] ?? 0
        if (tirage <= 0) {
          kind = k
          break
        }
      }
      if (!kind) kind = kinds[kinds.length - 1]
      if (!kind) continue

      const y = hauteurSecuritaire(ile, x, z)
      if (kind === 'coquillage' && y > 3) continue
      if ((kind === 'herbe' || kind === 'fleur') && ile.pente(x, z) > 0.6) continue

      const bornes: [number, number] = ECHELLE[kind] ?? [0.8, 1.2]
      props.push({
        id: id++,
        kind,
        x,
        z,
        y,
        rot: a.entre(0, Math.PI * 2),
        scale: a.entre(bornes[0], bornes[1]),
        teinte: teintePour(kind, def.biome, a),
      })
    }
  }
  return props
}

/** Ce que `genererPropsIle2` demande a l'ile 2, et rien de plus. */
export interface Ile2Semeur extends ITerrain {
  readonly x0: number
  readonly z0: number
  readonly cote: number
  regions(): readonly RegionDef[]
  regionAuPoint(x: number, z: number): RegionDef | null
}
